import React, { createContext, Fragment, useEffect, useState, useMemo } from 'react'
import { useLazyQuery } from 'components/graphql'
import { ContentBlock } from 'components/ContentBlock'
import { Row, Col } from 'react-flexbox-grid'
import Divider from 'components/Divider'
import { TextEditor } from 'components/forms'
import { DataTable } from 'components/DataTable'
import {
	bill_bills,
	bill_items
	// bill_discounts
} from '../tableColumns'
import ClosingBillFilter from '../fragments/ClosingBillFilter'
import BillCancel from '../fragments/BillCancel'
import listBills from '../gql/listBills'
import listBillsItems from '../gql/listBillsItems'
import { useTranslation } from 'react-i18next'
import useLocale from 'hooks/useLocale'
import { useTheme } from 'styled-components'
import { useModalBillPdf } from 'hooks/useModalBillPdf'

export const ReceiptContext = createContext()

const Receipt = (props) => {
	const [t] = useTranslation()
	const locale = useLocale()
	const theme = useTheme()
	const [filters, setFilters] = useState()
	const [displayBillDataTable, setDisplayBillDataTable] = useState(false)
	const [billData, setBillData] = useState()
	const [billListData, setBillListData] = useState()
	const {
		handlePdfClick, pdfsLoading,
		closeModal, ModalComponent
	} = useModalBillPdf()

	const [getBillItemsData, { data: billItemsData, queryField }] = useLazyQuery(listBillsItems)

	const handleSelectBill = (table, { original }) => {
		const globalCreditNames = props.appData.creditCards
		const clientButtonName = original.cashRegister?.parameters?.creditCards?.clientButtonName || []
		const crCreditNames = [...clientButtonName]
			.sort(({ position: pos1 }, { position: pos2 }) => pos2 - pos1)
			.map(({ value }) => value)

		const creditNames = crCreditNames
			.map((val, i) => val || globalCreditNames[i])
			.concat(globalCreditNames.slice(crCreditNames.length))
			.reduce((acc, val, i) => {
				acc[`TotalInCredit${i + 1}`] = val
				return acc
			}, {})

		setBillData({
			...original,
			creditNames
		})
		getBillItemsData({ variables: { id: original?.id }, fetchPolicy: 'cache-first' })
	}

	const cashRegisterId = props?.data?.cashRegisterId
	const billsCols = useMemo(() => bill_bills(t, locale, handlePdfClick, pdfsLoading, cashRegisterId, billListData), [t, locale, pdfsLoading, cashRegisterId, billListData])

	useEffect(()=>{
		setDisplayBillDataTable(false)
		setBillData({ ...billData, id: null })
	}, [filters])

	useEffect(()=> {
		if(billData?.id && !displayBillDataTable) {
			setDisplayBillDataTable(true)
		}
	}, [billData])

	const billItems = billItemsData?.[queryField]?.items ?
		billItemsData[queryField].items
			.filter(({ flag }) => flag !== 'Deleted')
			.map(({ flag, ...rest }) => {
				return {
					...rest,
					flag: billData.creditNames[flag] && flag !== 'TotalInCredit10' ? billData.creditNames[flag] : flag
				}
			}) : []

	//open PDF file
	if(closeModal) {
		return ModalComponent
	}

	return(
		<Fragment>
			<ReceiptContext.Provider value={{ setFilters }}>
				<ContentBlock>
					<DataTable
						label={t('closing_section.sub_sections.bill')}
						height={400}
						columns={billsCols}
						withSearch
						onSelect={handleSelectBill}
						isSelected={bill => bill.id === billData?.id}
						selectRow={{
							selected: billData?.id
						}}
						onRowClick={handleSelectBill}
						getTrProps={(_, { original }) => original.isCancelled ? { style: { background: theme.errorColor } } : {}}
						{...props.data.dateInterval ? {
							remoteOptions: {
								query: listBills,
								withFilter: {
									withButtons: false,
									FilterComponent: ClosingBillFilter,
									width: 90,
									height: 80
								},
								variables: {
									filter: {
										period: {
											from: props.data.dateInterval.from,
											to: props.data.dateInterval.to
										},
										cashRegisterIds: [props.data.cashRegisterId]
									}
								},
								onCompleted: setBillListData,
								cursorsLimitOnly: true // variables.params.limit causes an error in sql
							} }
							: { data: [] }
						}
					/>
				</ContentBlock>
				{ displayBillDataTable &&
				<Fragment>
					<ContentBlock>
						<DataTable
							label={billData.user ? `${billData.user.code} ${billData.user.name}` : t('closing_section.strings.no_cashier')}
							columns={bill_items(t, locale)}
							data={billItems}
						/>
						<Divider height={20}/>
						<BillCancel
							setBillData={setBillData}
							billData={billData}
							listClosingsVariables={props.listQueryRef?.current.variables}
						/>
					</ContentBlock>
					<ContentBlock>
						{/*<p>Preisminderung</p>
						<DataTable
							columns={bill_discounts}
							data={tableDataGenerator(bill_discounts, 0)}
						/>
						<Divider height={20}/>*/}
						<Row>
							<Col xs={6}>
								<TextEditor
									width={400}
									height={450}
									key={billData.id}
									label={t('closing_section.strings.bill_additional_info')}
									defaultText={billData.eft ? `<pre>${billData.eft}</pre>` : ''}
									readOnly
									placeholder={t('closing_section.placeholders.no_bill_info')}
									withToolbar={false}
									noForm
									monospace
								/>
							</Col>
							<Col xs={6}>
								<TextEditor
									width={400}
									height={450}
									key={billData.id}
									label={t('closing_section.strings.dealer_receipt')}
									defaultText={billData.rfid ? `<pre>${billData.rfid}</pre>` : ''}
									readOnly
									placeholder={t('closing_section.placeholders.no_bill_info')}
									withToolbar={false}
									noForm
									monospace
								/>
							</Col>
						</Row>
					</ContentBlock>
				</Fragment>
				}
			</ReceiptContext.Provider>
		</Fragment>
	)
}

export default Receipt
